import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { adminGetConfig, adminUpdateConfig } from "../../../redux/actions/secondaryMarketActions";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

const defaultFormData = {
  secondary_trading_enabled: true,
  min_holding_period_days: 90,
  platform_fee_percentage: 5.0,
  listing_fee_flat: 0,
  max_premium_percentage: 50,
  max_discount_percentage: 50,
  requires_issuer_approval: false,
  compliance_enabled: true,
};

const SecondaryMarketConfig = () => {
  const dispatch = useDispatch();
  const { config } = useSelector((state) => state.secondaryMarket);

  const [campaignId, setCampaignId] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [formData, setFormData] = useState({ ...defaultFormData });

  const handleLoadConfig = () => {
    if (!campaignId) {
      Swal.fire("Error", "Please enter a Campaign ID", "error");
      return;
    }
    setConfigLoaded(false);
    dispatch(adminGetConfig(campaignId))
      .then((res) => {
        const d = res.data;
        setFormData({
          secondary_trading_enabled: d.secondary_trading_enabled ?? defaultFormData.secondary_trading_enabled,
          min_holding_period_days: d.min_holding_period_days ?? defaultFormData.min_holding_period_days,
          platform_fee_percentage: d.platform_fee_percentage ?? defaultFormData.platform_fee_percentage,
          listing_fee_flat: d.listing_fee_flat ?? defaultFormData.listing_fee_flat,
          max_premium_percentage: d.max_premium_percentage ?? defaultFormData.max_premium_percentage,
          max_discount_percentage: d.max_discount_percentage ?? defaultFormData.max_discount_percentage,
          requires_issuer_approval: d.requires_issuer_approval ?? defaultFormData.requires_issuer_approval,
          compliance_enabled: d.compliance_enabled ?? defaultFormData.compliance_enabled,
        });
        setConfigLoaded(true);
      })
      .catch(() => {
        setFormData({ ...defaultFormData });
        setConfigLoaded(true);
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!campaignId) {
      Swal.fire("Error", "Please enter a Campaign ID", "error");
      return;
    }
    dispatch(adminUpdateConfig(campaignId, formData))
      .then(() => Swal.fire("Success", "Configuration updated", "success"))
      .catch(() => Swal.fire("Error", "Failed to update configuration", "error"));
  };

  const fieldStyle = { marginBottom: "16px" };
  const labelStyle = { display: "block", fontWeight: 600, marginBottom: "4px", fontSize: "14px" };
  const inputStyle = { padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", width: "100%", maxWidth: "300px" };

  return (
    <PageShell>
      <PageHead title="Secondary market · configuration" />
      <div className="list">
      <div className="listContainer">
        <div style={{ padding: "20px" }}>
          <form onSubmit={handleSubmit}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Campaign ID</label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="number" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} style={inputStyle} placeholder="Enter campaign ID" required />
                <button
                  type="button"
                  onClick={handleLoadConfig}
                  disabled={config.loading}
                  style={{
                    padding: "8px 16px", background: "#455a64", color: "#fff", border: "none",
                    borderRadius: "4px", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap",
                    opacity: config.loading ? 0.6 : 1,
                  }}
                >
                  {config.loading ? "Loading..." : "Load Config"}
                </button>
              </div>
              {configLoaded && !config.data && <small style={{ color: "#f57c00" }}>No existing config — using defaults (new config will be created on save).</small>}
            </div>

            <div style={fieldStyle}>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="secondary_trading_enabled" checked={formData.secondary_trading_enabled} onChange={handleChange} />
                Secondary Trading Enabled
              </label>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Min Holding Period (days)</label>
              <input type="number" name="min_holding_period_days" value={formData.min_holding_period_days} onChange={handleChange} style={inputStyle} min="0" />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Platform Fee (%)</label>
              <input type="number" name="platform_fee_percentage" value={formData.platform_fee_percentage} onChange={handleChange} style={inputStyle} min="0" max="100" step="0.01" />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Listing Fee (flat CAD)</label>
              <input type="number" name="listing_fee_flat" value={formData.listing_fee_flat} onChange={handleChange} style={inputStyle} min="0" step="0.01" />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Max Premium (%)</label>
              <input type="number" name="max_premium_percentage" value={formData.max_premium_percentage} onChange={handleChange} style={inputStyle} min="0" max="200" />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Max Discount (%)</label>
              <input type="number" name="max_discount_percentage" value={formData.max_discount_percentage} onChange={handleChange} style={inputStyle} min="0" max="100" />
            </div>

            <div style={fieldStyle}>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="requires_issuer_approval" checked={formData.requires_issuer_approval} onChange={handleChange} />
                Requires Issuer Approval
              </label>
            </div>

            <div style={fieldStyle}>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="compliance_enabled" checked={formData.compliance_enabled} onChange={handleChange} />
                Compliance Enabled (NI 45-102)
              </label>
            </div>

            <button
              type="submit"
              disabled={config.updating}
              style={{
                padding: "10px 24px", background: "#1976d2", color: "#fff", border: "none",
                borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "14px",
                opacity: config.updating ? 0.6 : 1,
              }}
            >
              {config.updating ? "Saving..." : "Save Configuration"}
            </button>
          </form>
        </div>
      </div>
      </div>
    </PageShell>
  );
};

export default SecondaryMarketConfig;
